import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";

/**
 * Equal-width columns that fill the row, so every tile, the last row's included, is the
 * same width. `sm` and `md` auto-fill: as many as fit at the size's floor, each then
 * widened to share the leftover space, so a lone file stays tile-sized instead of
 * stretching across the row. `dense` is a fixed column ladder on the grid's own width.
 */
const attachmentGridVariants = cva(
  cn(
    /* sorted */
    "grid",
    "items-start gap-2",
  ),
  {
    variants: {
      size: {
        // 88px floor — asides and side panels.
        sm: "grid-cols-[repeat(auto-fill,minmax(5.5rem,1fr))]",
        // 144px floor — the design's tile; the gallery picker.
        md: "grid-cols-[repeat(auto-fill,minmax(9rem,1fr))]",
        // md's step widths (every 9rem tile + 0.5rem gap) with one more column at each —
        // Files tabs. Needs the `@container/attachment-grid` wrapper below.
        dense: cn(
          /* sorted */
          "grid-cols-3",
          "@min-[28rem]/attachment-grid:grid-cols-4",
          "@min-[37.5rem]/attachment-grid:grid-cols-5",
          "@min-[47rem]/attachment-grid:grid-cols-6",
          "@min-[56.5rem]/attachment-grid:grid-cols-7",
          "@min-[66rem]/attachment-grid:grid-cols-8",
          "@min-[75.5rem]/attachment-grid:grid-cols-9",
          "@min-[85rem]/attachment-grid:grid-cols-10",
          "@min-[94.5rem]/attachment-grid:grid-cols-11",
        ),
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

export type AttachmentGridSize = NonNullable<
  VariantProps<typeof attachmentGridVariants>["size"]
>;

export function AttachmentGrid({
  size,
  className,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof attachmentGridVariants>) {
  const grid = (
    <div
      className={cn(attachmentGridVariants({ size }), className)}
      {...props}
    />
  );
  // A grid can't query its own width. Only `dense` gets the container: inline-size
  // containment would collapse an auto-fill grid inside a shrink-to-fit parent.
  if (size !== "dense") return grid;
  return <div className="@container/attachment-grid">{grid}</div>;
}
